// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InfoOutlined as InfoOutlinedIcon } from '@/constants/hardfin-standard-icons';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

interface ClusterBraceProps {
  label: string;
  fullLabel: string;
  description: string;
}

// Visual brace that sits above a cluster of summary cards (ADS / ADC),
// labelling the group and surfacing a descriptive tooltip on hover. The
// brace shape is drawn via three absolute-positioned 1px dividers —
// horizontal spine + two end caps + midpoint marker.
export function ClusterBrace(props: ClusterBraceProps) {
  const { label, fullLabel, description } = props;
  return (
    <Box
      sx={{
        position: 'relative',
        my: 1,
        mx: 1,
        height: 40,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <Tooltip title={description} arrow placement="top">
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ position: 'absolute', top: 0, cursor: 'help' }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.primary',
              fontWeight: 'bold',
              lineHeight: 1,
            }}
          >
            {fullLabel}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', lineHeight: 1 }}
          >
            ({label})
          </Typography>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Stack>
      </Tooltip>
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: 'divider',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '1px',
          height: 8,
          backgroundColor: 'divider',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '1px',
          height: 8,
          backgroundColor: 'divider',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          width: '1px',
          height: 8,
          backgroundColor: 'divider',
        }}
      />
    </Box>
  );
}
